'use client';

import { useCallback, useMemo } from 'react';
import {
  T3kPlayer,
  type NonEmptyArray,
  type PlayerAssets,
  type PlayerEvent,
  type PlayerLayout,
  type PlayerTheme,
  type PlayerTone,
} from 'neural-amp-modeler-wasm';
import 'neural-amp-modeler-wasm/styles.css';
import { useOAuthFlow, useOAuthHref } from '@/contexts/OAuthFlowContext';
import { usePlayerConfig } from '@/contexts/PlayerConfigContext';
import { useAudioEngineSupport } from '@/hooks/useAudioEngineSupport';
import { analytics } from '@/lib/analytics';
import { FORMAT, GEAR } from '@/utils/constants';
import { companionFor } from '@/utils/player/gear-matrix';
import { toneAssetsClient } from '@/utils/player/tone-assets-client';
import { createClient } from '@/utils/supabase/client';
import {
  isBassTone,
  type PlayableModelRow,
  type PlayableToneRow,
  toPlayerAssets,
  toPlayerTone,
  tonePath,
} from '@/utils/player/to-player-tone';
import { useToneSurface } from '../../ToneSection/ToneSurface';
import { PlayerInfo } from './components/PlayerInfo';

export type TonePlayerEntry =
  /** Eager: the tone's models are already loaded (tone page). */
  | { tone: PlayableToneRow; models: PlayableModelRow[]; lazy?: undefined }
  /**
   * Lazy: fetched on first play (cards). `models` loads the tone's full A2
   * list; `featured` loads only the first playable model (mini players).
   */
  | { tone: PlayableToneRow; lazy: 'models' | 'featured'; models?: undefined };

const NO_A2 = 'This tone has no A2 models to preview yet.';
const NOT_PLAYABLE = 'This tone has nothing the player can preview.';

export interface TonePlayerProps {
  tones: NonEmptyArray<TonePlayerEntry>;
  /** Share token from the page URL; opens a private tone's files. */
  token?: string | null;
  layout: PlayerLayout;
  /**
   * `tone.played`'s `from`. Defaults to the surrounding ToneSurface's row
   * name, then `search_page`.
   */
  from?: string;
  /** Show the "Plug in and play" tab. Off inside the Select plugin webview. */
  live?: boolean;
  /** Token sheet. The site is dark; embeds let the host pick `light`. */
  theme?: PlayerTheme;
  disabled?: boolean | { reason?: string };
  isLoading?: boolean;
  /** `compact` / `full` under a card: no top border or radius. */
  docked?: boolean;
  /** Render the tone header (`full` only). Off on the tone page, which has its own. */
  meta?: boolean;
  /**
   * The "what is this" info button in the header. Off in embeds, whose frame
   * is too short for the modal it opens.
   */
  info?: boolean;
  /** `mini`: button diameter. */
  size?: number;
  /**
   * "Powered by TONE3000" mark. On for the tone page player and embeds on
   * other sites; off for the docked players in search results and profiles,
   * where it would repeat on every card.
   */
  attribution?: boolean;
  /** Stable id to correlate events across remounts. */
  id?: string;
  /**
   * Where tone / author links open. `_self` on the site; embeds on other
   * sites use `_blank` so the host page stays put.
   */
  linkTarget?: '_self' | '_blank';
  /**
   * `compact` only: start expanded. Embeds pass this when the host page can't
   * resize the frame, so the controls aren't trapped behind the toggle.
   */
  defaultExpanded?: boolean;
  /**
   * Receives every player event after TonePlayer's own handling (analytics,
   * PlayerConfig). The embed route forwards these to the host page.
   */
  onEvent?: (event: PlayerEvent) => void;
}

const ENGINE_UNSUPPORTED = "Previews aren't supported in this browser.";

/**
 * The only tone3000 component that renders `neural-amp-modeler-wasm`. Owns the
 * analytics capture, the per-instrument default persistence (PlayerConfig)
 * and the engine-support gate; everything else is the OSS player.
 */
export const TonePlayer = ({
  tones,
  token,
  layout,
  from,
  live = true,
  theme,
  disabled = false,
  isLoading = false,
  docked = false,
  meta = true,
  info = true,
  size,
  attribution = false,
  id,
  linkTarget = '_self',
  defaultExpanded,
  onEvent: onEventProp,
}: TonePlayerProps) => {
  const { config, updateConfig } = usePlayerConfig();
  const supabase = useMemo(() => createClient(), []);
  const engineSupported = useAudioEngineSupport();
  const surface = useToneSurface()?.surface;
  const { authorizationId } = useOAuthFlow();
  const oauthHref = useOAuthHref();
  const playedFrom = from ?? surface ?? 'search_page';

  // Inside the Select flow every link must keep the authorization id.
  const hrefFor = useCallback(
    (tone: PlayableToneRow) =>
      authorizationId
        ? oauthHref(`/api/v1/select/tones/${tonePath(tone)}`)
        : `/tones/${tonePath(tone)}`,
    [authorizationId, oauthHref]
  );
  const userHrefFor = useCallback(
    (tone: PlayableToneRow) =>
      tone.user?.username
        ? authorizationId
          ? oauthHref(`/api/v1/select/${tone.user.username}`)
          : `/${tone.user.username}`
        : undefined,
    [authorizationId, oauthHref]
  );

  // Eager entries are mapped here with the user's defaults; the player
  // resolves each tone once and keeps that, so later config changes (made
  // through this very player) don't reset the selection.
  const { playerTones, unplayableReason } = useMemo(() => {
    let unplayableReason: string | undefined;
    const playerTones = tones.map((entry) => {
      const { tone } = entry;
      let assets: PlayerAssets | (() => Promise<PlayerAssets>);
      if (entry.lazy) {
        assets = toneAssetsClient(supabase, tone, config, {
          featuredOnly: entry.lazy === 'featured',
        });
      } else {
        const eager = toPlayerAssets(entry.models, tone, config, token);
        if (eager) {
          assets = eager;
        } else {
          const reason = tone.platform === FORMAT.NAM ? NO_A2 : NOT_PLAYABLE;
          if (tones.length === 1) unplayableReason = reason;
          assets = () => Promise.reject(new Error(reason));
        }
      }
      return toPlayerTone(tone, assets, { meta, hrefFor, userHrefFor });
    }) as NonEmptyArray<PlayerTone>;
    return { playerTones, unplayableReason };
    // `config` is intentionally not a dependency: see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tones, token, meta, hrefFor, userHrefFor, supabase]);

  const onEvent = useCallback(
    (event: PlayerEvent) => {
      switch (event.type) {
        case 'play':
          // Same shape as before v3: `is_live` only present for live plays.
          analytics.capture(
            'tone.played',
            event.mode === 'live' ? { from: playedFrom, is_live: true } : { from: playedFrom }
          );
          break;
        case 'selection_change': {
          const entry = tones.find(({ tone }) => String(tone.id) === event.toneId);
          if (!entry) break;
          const inst = isBassTone(entry.tone.tags) ? 'bass' : 'guitar';
          const platform = entry.tone.platform as FORMAT;
          // Only remember choices from the *demo* side of the pairing: the
          // amp picked for an IR tone or ahead of a cabinet capture, the cab
          // picked behind an amp or a pedal, and the DI either way. The
          // tone's own models aren't a preference, and neither is "None" —
          // except behind a pedal, whose cab starts at None and is its own pick.
          const cabDefault = companionFor(
            (entry.tone.gear as GEAR | null) ?? undefined,
            FORMAT.NAM
          ).default;
          if (event.field === 'input') {
            updateConfig({ inputs: { ...config.inputs, [inst]: event.value } });
          } else if (event.field === 'model' && platform === FORMAT.IR) {
            updateConfig({ models: { ...config.models, [inst]: event.value } });
          } else if (event.field === 'preModel' && event.value.url) {
            updateConfig({ models: { ...config.models, [inst]: event.value } });
          } else if (event.field === 'ir' && platform === FORMAT.NAM) {
            if (cabDefault === 'pedal') {
              updateConfig({ pedalIrs: { ...config.pedalIrs, [inst]: event.value } });
            } else if (cabDefault === 'user' && event.value.url) {
              updateConfig({ ampIrs: { ...config.ampIrs, [inst]: event.value } });
            }
          }
          break;
        }
        case 'error':
          console.error('[TonePlayer]', event.code, event.message);
          break;
        default:
          break;
      }
      onEventProp?.(event);
    },
    [tones, playedFrom, config, updateConfig, onEventProp]
  );

  const effectiveDisabled = !engineSupported
    ? { reason: ENGINE_UNSUPPORTED }
    : unplayableReason
      ? { reason: unplayableReason }
      : disabled;

  return (
    <T3kPlayer
      id={id}
      tones={playerTones}
      layout={layout}
      live={live}
      theme={theme}
      slimSize={0.5}
      disabled={effectiveDisabled}
      isLoading={isLoading}
      docked={docked}
      size={size}
      attribution={attribution}
      linkTarget={linkTarget}
      defaultExpanded={defaultExpanded}
      headerSlot={layout === 'mini' || !info ? undefined : <PlayerInfo />}
      onEvent={onEvent}
    />
  );
};
